import React, { useState, useEffect } from 'react';
import { OnlineClass } from '../types';
import { supabase } from '../supabase';
import { sendClassRegistrationConfirmation } from '../services/classRegistrationNotifications';

interface OnlineClassCardProps {
  classData: OnlineClass;
  showRegisterButton?: boolean;
  onRegistrationChange?: () => void;
}

export default function OnlineClassCard({ classData, showRegisterButton = false, onRegistrationChange }: OnlineClassCardProps) {
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [studentName, setStudentName] = useState('');
  const [studentEmail, setStudentEmail] = useState('');
  const [studentPhone, setStudentPhone] = useState('');
  const [verifyName, setVerifyName] = useState('');
  const [registering, setRegistering] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [verifyError, setVerifyError] = useState('');
  const [registeredCount, setRegisteredCount] = useState(classData.registeredStudents || 0);

  const classDate = new Date(classData.date);
  const today = new Date();
  const isUpcoming = classDate >= today;

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (timeStr: string) => {
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const minute = parseInt(minutes);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minute.toString().padStart(2, '0')} ${ampm}`;
  };

  const gradients = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  ];

  const gradientIdx = classData.teacherName.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % gradients.length;

  useEffect(() => {
    const fetchRegistrationCount = async () => {
      try {
        const { count } = await supabase.from('class_registrations').select('id', { count: 'exact', head: true }).eq('class_id', classData.id);
        setRegisteredCount(count ?? 0);
      } catch { /* silently fail */ }
    };
    if (classData.id) fetchRegistrationCount();
  }, [classData.id]);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegistering(true);
    setError('');
    setSuccess('');

    try {
      if (!studentName || !studentEmail) {
        setError('Please fill in name and email');
        setRegistering(false);
        return;
      }

      // Check if already registered
      const { data: existing } = await supabase.from('class_registrations')
        .select('id').eq('class_id', classData.id).eq('student_email', studentEmail.toLowerCase()).maybeSingle();
      if (existing) {
        setError('You have already registered for this class');
        setRegistering(false);
        return;
      }

      // Check if class is full
      if (classData.maxStudents && registeredCount >= classData.maxStudents) {
        setError('This class is full');
        setRegistering(false);
        return;
      }

      // Register student
      const { data: registration, error: insertErr } = await supabase.from('class_registrations').insert({
        class_id: classData.id,
        student_name: studentName,
        student_email: studentEmail.toLowerCase(),
        student_phone: studentPhone,
      }).select('id').single();
      if (insertErr) throw insertErr;

      let successMessage = 'Successfully registered! Your seat is confirmed.';

      try {
        const emailResult = await sendClassRegistrationConfirmation(registration.id);

        if (emailResult?.emails?.student?.status === 'sent') {
          successMessage = 'Successfully registered! A confirmation email has been sent.';
        } else {
          successMessage = 'Successfully registered! Your seat is confirmed, but the confirmation email is still pending.';
        }
      } catch (emailError) {
        console.error('Registration email failed:', emailError);
        successMessage = 'Successfully registered! Your seat is confirmed, but the confirmation email could not be sent right now.';
      }

      setSuccess(successMessage);
      setRegisteredCount(prev => prev + 1);
      setStudentName('');
      setStudentEmail('');
      setStudentPhone('');

      if (onRegistrationChange) {
        onRegistrationChange();
      }

      setTimeout(() => {
        setShowRegisterModal(false);
        setSuccess('');
      }, 3000);
    } catch (error: any) {
      console.error('Error registering:', error);
      setError('Failed to register: ' + error.message);
    } finally {
      setRegistering(false);
    }
  };

  const isFull = classData.maxStudents ? registeredCount >= classData.maxStudents : false;

  const handleVerifyAndJoin = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerifying(true);
    setVerifyError('');

    try {
      if (!verifyName.trim()) {
        setVerifyError('Please enter your registered name');
        setVerifying(false);
        return;
      }

      // Check if this name is registered for this class
      const { data: regs } = await supabase.from('class_registrations').select('student_name').eq('class_id', classData.id);
      const isRegistered = (regs || []).some(r => r.student_name.toLowerCase().trim() === verifyName.toLowerCase().trim());
      if (!isRegistered) {
        setVerifyError('Name not found in registrations. Please register first or check your spelling.');
        setVerifying(false);
        return;
      }

      // If verified, open the class link
      if (classData.classLink) {
        window.open(classData.classLink, '_blank');
        setShowVerifyModal(false);
        setVerifyName('');
      }
    } catch (error: any) {
      console.error('Error verifying registration:', error);
      setVerifyError('Failed to verify: ' + error.message);
    } finally {
      setVerifying(false);
    }
  };

  const handleJoinClassClick = () => {
    if (showRegisterButton) {
      // For home page - require verification
      setShowVerifyModal(true);
    } else {
      // For teacher dashboard - direct access
      if (classData.classLink) {
        window.open(classData.classLink, '_blank');
      }
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden hover:shadow-lg transition-all duration-300 flex flex-col h-full">
      {/* Header with Gradient */}
      <div
        style={{ background: gradients[gradientIdx] }}
        className="h-24 flex items-center justify-between p-6 text-white relative"
      >
        <div className="flex-1">
          <div className="text-sm font-semibold opacity-90">{classData.subject}</div>
          <h3 className="text-lg font-bold line-clamp-2">{classData.title}</h3>
        </div>
        {isUpcoming && (
          <div className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ml-2">
            Upcoming
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6 flex-1 flex flex-col gap-4">
        {/* Teacher Info */}
        <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
          <div
            style={{ background: gradients[gradientIdx] }}
            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
          >
            {classData.teacherName.charAt(0)}
          </div>
          <div>
            <p className="text-xs text-slate-500">Teacher</p>
            <p className="text-sm font-bold text-navy">{classData.teacherName}</p>
          </div>
        </div>

        {/* Description */}
        {classData.description && (
          <p className="text-sm text-slate-600 line-clamp-2">{classData.description}</p>
        )}

        {/* Details */}
        <div className="space-y-3">
          <div className="flex items-center gap-3 text-sm">
            <span className="material-symbols-outlined text-base text-primary">calendar_today</span>
            <div>
              <p className="text-slate-500 text-xs">Date</p>
              <p className="font-semibold text-navy">{formatDate(classData.date)}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 text-sm">
            <span className="material-symbols-outlined text-base text-primary">schedule</span>
            <div>
              <p className="text-slate-500 text-xs">Time</p>
              <p className="font-semibold text-navy">{formatTime(classData.time)}</p>
            </div>
          </div>

          {classData.duration && (
            <div className="flex items-center gap-3 text-sm">
              <span className="material-symbols-outlined text-base text-primary">timer</span>
              <div>
                <p className="text-slate-500 text-xs">Duration</p>
                <p className="font-semibold text-navy">{classData.duration} minutes</p>
              </div>
            </div>
          )}

          {classData.maxStudents && (
            <div className="flex items-center gap-3 text-sm">
              <span className="material-symbols-outlined text-base text-primary">people</span>
              <div>
                <p className="text-slate-500 text-xs">Capacity</p>
                <p className="font-semibold text-navy">
                  {registeredCount} / {classData.maxStudents} Students
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="p-6 border-t border-slate-100 bg-slate-50 flex flex-col sm:flex-row gap-3">
        {showRegisterButton ? (
          <>
            <button
              onClick={() => setShowRegisterModal(true)}
              disabled={isFull || !isUpcoming}
              className={`flex-1 py-2 rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                isFull || !isUpcoming
                  ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  : 'bg-green-500 text-white hover:bg-green-600'
              }`}
            >
              <span className="material-symbols-outlined text-sm">person_add</span>
              {isFull ? 'Class Full' : !isUpcoming ? 'Class Ended' : 'Register'}
            </button>
            {classData.classLink && isUpcoming && (
              <button
                onClick={handleJoinClassClick}
                className="flex-1 bg-primary text-white py-2 rounded-lg font-bold text-sm hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
              >
                <span className="material-symbols-outlined text-sm">video_call</span>
                Join Class
              </button>
            )}
          </>
        ) : (
          <>
            {classData.classLink ? (
              <a
                href={classData.classLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-primary text-white py-2 rounded-lg font-bold text-sm hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
              >
                <span className="material-symbols-outlined text-sm">video_call</span>
                Join Class
              </a>
            ) : (
              <button
                disabled
                className="flex-1 bg-slate-300 text-slate-500 py-2 rounded-lg font-bold text-sm cursor-not-allowed flex items-center justify-center gap-2"
              >
                <span className="material-symbols-outlined text-sm">info</span>
                Link TBA
              </button>
            )}
          </>
        )}
      </div>

      {/* Registration Modal */}
      {showRegisterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-5 sm:p-8 max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-start sm:items-center justify-between mb-6 gap-4">
              <h3 className="text-xl sm:text-2xl font-extrabold text-navy">Register for Class</h3>
              <button
                onClick={() => {
                  setShowRegisterModal(false);
                  setError('');
                  setSuccess('');
                }}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <span className="material-symbols-outlined text-2xl">close</span>
              </button>
            </div>

            <div className="mb-4 p-3 bg-indigo-50 rounded-lg">
              <p className="text-sm font-bold text-navy">{classData.title}</p>
              <p className="text-xs text-slate-600 mt-1">
                {classData.teacherName} • {formatDate(classData.date)} at {formatTime(classData.time)}
              </p>
            </div>

            <form onSubmit={handleRegister} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {success && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                  {success}
                </div>
              )}

              <div>
                <label className="block text-sm font-bold text-navy mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="register-student-name"
                  name="studentName"
                  value={studentName}
                  onChange={(e) => setStudentName(e.target.value)}
                  placeholder="Enter your full name"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-navy mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="register-student-email"
                  name="studentEmail"
                  value={studentEmail}
                  onChange={(e) => setStudentEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-navy mb-2">
                  Phone Number (Optional)
                </label>
                <input
                  type="tel"
                  id="register-student-phone"
                  name="studentPhone"
                  value={studentPhone}
                  onChange={(e) => setStudentPhone(e.target.value)}
                  placeholder="07XXXXXXXX"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  type="submit"
                  disabled={registering}
                  className="flex-1 bg-green-500 text-white py-3 rounded-lg font-bold hover:bg-green-600 transition-all disabled:opacity-50"
                >
                  {registering ? 'Registering...' : 'Confirm Registration'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowRegisterModal(false);
                    setError('');
                    setSuccess('');
                  }}
                  className="flex-1 border border-slate-300 text-slate-700 py-3 rounded-lg font-bold hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Verification Modal for Joining Class */}
      {showVerifyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-5 sm:p-8 max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-start sm:items-center justify-between mb-6 gap-4">
              <h3 className="text-xl sm:text-2xl font-extrabold text-navy">Verify Registration</h3>
              <button
                onClick={() => {
                  setShowVerifyModal(false);
                  setVerifyError('');
                  setVerifyName('');
                }}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <span className="material-symbols-outlined text-2xl">close</span>
              </button>
            </div>

            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800 flex items-start gap-2">
                <span className="material-symbols-outlined text-base">info</span>
                <span>Please enter your registered name to join the class. Make sure you have registered first!</span>
              </p>
            </div>

            <div className="mb-4 p-3 bg-indigo-50 rounded-lg">
              <p className="text-sm font-bold text-navy">{classData.title}</p>
              <p className="text-xs text-slate-600 mt-1">
                {classData.teacherName} • {formatDate(classData.date)} at {formatTime(classData.time)}
              </p>
            </div>

            <form onSubmit={handleVerifyAndJoin} className="space-y-4">
              {verifyError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
                  <span className="material-symbols-outlined text-base">error</span>
                  <span>{verifyError}</span>
                </div>
              )}

              <div>
                <label className="block text-sm font-bold text-navy mb-2">
                  Enter Your Registered Name *
                </label>
                <input
                  type="text"
                  id="verify-student-name"
                  name="verifyName"
                  value={verifyName}
                  onChange={(e) => setVerifyName(e.target.value)}
                  placeholder="Exact name you used to register"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                  autoFocus
                />
                <p className="text-xs text-slate-500 mt-1">
                  Enter the exact name you used when registering for this class
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  type="submit"
                  disabled={verifying}
                  className="flex-1 bg-primary text-white py-3 rounded-lg font-bold hover:bg-blue-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {verifying ? (
                    'Verifying...'
                  ) : (
                    <>
                      <span className="material-symbols-outlined text-sm">verified</span>
                      Verify & Join
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowVerifyModal(false);
                    setVerifyError('');
                    setVerifyName('');
                  }}
                  className="flex-1 border border-slate-300 text-slate-700 py-3 rounded-lg font-bold hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
              </div>

              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowVerifyModal(false);
                    setShowRegisterModal(true);
                  }}
                  className="text-sm text-primary hover:underline"
                >
                  Haven't registered? Click here to register
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
